<template>
  <div class="files-table-container" @dragenter="$refs.fileUpload.dragging = true">
    <table class="full-width-table files-table">
      <tbody>
        <tr class="table-header">
          <th style="width: 30%">Name</th>
          <th style="width: auto">Date</th>
          <th style="width: 6rem"></th>
        </tr>
        <tr v-if="!loading && files.length > 0" v-for="file in files" :key="file.name" class="file-entry-row">
          <td>
            <div class="file-name-container" @click="getFile(entityid, type, file.name)">
              <img v-if="checkFileType(file.type) == 'image'" src="../assets/filetype-image.svg" />
              <img v-else-if="checkFileType(file.type) == 'pdf'" src="../assets/filetype-pdf.svg" />
              <img v-else-if="checkFileType(file.type) == 'word'" src="../assets/filetype-word.svg" />
              <img v-else-if="checkFileType(file.type) == 'excel'" src="../assets/filetype-excel.svg" />
              <img v-else-if="checkFileType(file.type) == 'email'" src="../assets/filetype-email.svg" />
              <img v-else src="../assets/filetype-other.svg" alt="" />
              <span class="file-name">{{ file.name }}</span>
            </div>
          </td>
          <td>
            <span>{{ formatDate(file.date) }}</span>
          </td>
          <td>
            <div class="actions-container" @click.stop>
              <div class="icon-button small-icon" @click="showRenameFile(file)" title="Rename">
                <img id="noHover" src="../assets/pencil.svg" alt="Rename" />
                <img id="hover" src="../assets/pencil-hover.svg" alt="Rename" />
              </div>
              <div class="icon-button small-icon" @click="confirmDeleteFile(file)" title="Delete">
                <img id="noHover" src="../assets/trash-bubble.svg" alt="Delete" />
                <img id="hover" src="../assets/trash-bubble-hover.svg" alt="Delete" />
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="!loading && files.length === 0" class="no-results">
          <td colspan="3">No files found</td>
        </tr>
        <tr v-if="loading" class="loading-row">
          <td colspan="3"><img src="../assets/spinner.svg" /></td>
        </tr>
      </tbody>
    </table>
    <FileUpload ref="fileUpload" :isPanel="false" @updateFileList="uploadFiles" />
  </div>

  <!-- Delete Confirmation -->
  <Confirmation
    v-if="showDeleteConfirm"
    @confirm="handleDeleteFile"
    @cancel="showDeleteConfirm = false"
    header="Delete File"
    message="This will permanently delete this file. Are you sure?"
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />

  <!-- Rename File -->
  <RenameFile
    v-if="showRenameConfirm"
    @closePopup="showRenameConfirm = false"
    @refetchFiles="refetchFiles"
    :filename="selectedFile?.name"
    :contactid="type === 'contact' ? entityid : null"
    :companyid="type === 'company' ? entityid : null" />
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import useAlerts from '@/composables/useAlerts';
import useContactFilesQuery from '@/composables/useContactFilesQuery';
import useContactFiles from '@/composables/useContactFiles';
import Confirmation from './Confirmation.vue';
import FileUpload from './FileUpload.vue';
import RenameFile from './RenameFile.vue';

export default {
  name: 'FilesTab',
  components: {
    Confirmation,
    FileUpload,
    RenameFile,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { addAlert } = useAlerts();
    const { fetchFiles, getFiles, refetchFiles } = useContactFilesQuery();
    const { uploadContactFiles, deleteFile, getFile } = useContactFiles();

    // Computed properties that get data from the composable
    const files = computed(() => {
      const filesData = getFiles(props.entityid, props.type);
      return filesData.data;
    });

    const loading = computed(() => {
      const filesData = getFiles(props.entityid, props.type);
      return filesData.loading;
    });

    const error = computed(() => {
      const filesData = getFiles(props.entityid, props.type);
      return filesData.error;
    });

    // Function to load files
    const loadFiles = () => {
      if (props.entityid && props.type) {
        fetchFiles(props.entityid, props.type);
      }
    };

    // Watch for prop changes and reload files
    watch(
      [() => props.entityid, () => props.type],
      () => {
        loadFiles();
      },
      { immediate: true }
    );

    // Watch for errors and show alerts
    watch(error, (newError) => {
      if (newError) {
        addAlert(`Error loading ${props.type} files`, 'high', 5);
      }
    });

    // Load files on mount
    onMounted(() => {
      loadFiles();
    });

    return {
      files,
      loading,
      addAlert,
      refetchFiles: () => refetchFiles(props.entityid, props.type),
      uploadContactFiles,
      deleteFile,
      getFile,
    };
  },
  data() {
    return {
      showRenameConfirm: false,
      showDeleteConfirm: false,
      selectedFile: null,
    };
  },
  methods: {
    async uploadFiles(files) {
      if (!files || files.length === 0) return;

      try {
        // Get the actual File objects from the FileUpload component ref
        const fileUploadComponent = this.$refs.fileUpload;
        if (fileUploadComponent && fileUploadComponent.dt && fileUploadComponent.dt.files) {
          const fileObjects = Array.from(fileUploadComponent.dt.files);

          if (fileObjects.length > 0) {
            await this.uploadContactFiles(fileObjects, this.entityid, this.type, true);
            // Clear the files from the FileUpload component
            fileUploadComponent.clearFiles();
            // Refetch files after successful upload
            this.refetchFiles();
          }
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        this.addAlert('Error uploading files', 'high', 5);
      }
    },
    formatDate(date) {
      if (!date) return '';

      try {
        // Try to parse as integer timestamp first
        const timestamp = parseInt(date);
        if (!isNaN(timestamp)) {
          const dateObj = new Date(timestamp);
          return dateObj.toLocaleDateString('en-US');
        }

        // Fall back to string parsing
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US');
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    },
    checkFileType(path) {
      const extension = path.split('.').pop().toLowerCase();
      switch (extension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        case 'pdf':
          return 'pdf';
        case 'doc':
        case 'docx':
          return 'word';
        case 'xls':
        case 'xlsx':
          return 'excel';
        case 'eml':
        case 'msg':
          return 'email';
        default:
          return 'other';
      }
    },
    confirmDeleteFile(file) {
      this.selectedFile = file;
      this.showDeleteConfirm = true;
    },
    async handleDeleteFile() {
      this.showDeleteConfirm = false;

      if (!this.selectedFile) return;

      try {
        // Delete the file using the composable
        await this.deleteFile(this.entityid, this.type, this.selectedFile.name);

        // Show success message
        this.addAlert('File deleted successfully', 'low', 3);

        // Refetch files to update the list
        this.refetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
        this.addAlert('Error deleting file', 'high', 5);
      }

      this.selectedFile = null;
    },
    showRenameFile(file) {
      this.selectedFile = file;
      this.showRenameConfirm = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.files-table-container {
  position: relative;

  .files-table {
    th:first-child,
    td:first-child {
      padding-left: 3.75rem;
    }

    .loading-row {
      td {
        background: white;
      }
    }

    .file-name-container {
      text-wrap: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .file-name {
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .actions-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-right: 1rem;
    }
  }
}
</style>
